/*
 Copyright 2013-2017 the original author or authors from the JHipster project.

 This file is part of the JHipster project, see https://jhipster.github.io/
 for more information.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

 http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.
 */
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { JhiConfigService } from '../config.service';
import { JhiEventManager } from '../service';

@Injectable({
    providedIn: 'root'
})
export class JhiLanguageService {

    currentLang: string;
    readonly defaultLanguage = 'en';

    constructor(private translateService: TranslateService,
                private configService: JhiConfigService,
                private eventManager: JhiEventManager) {
        this.init();
    }

    init() {
        const config = this.configService.getConfig();
        this.currentLang = config.defaultI18nLang;
        this.translateService.setDefaultLang(this.currentLang);
        this.translateService.use(this.currentLang);
    }

    changeLanguage(languageKey: string) {
        if (this.currentLang !== languageKey) {
            this.currentLang = languageKey;
            this.configService.CONFIG_OPTIONS.defaultI18nLang = languageKey;
            this.translateService.use(this.currentLang);
            this.setLangCookie();
        }
    }

    getCurrentLanguage(): string {
        // 1. Check Cookie
        const cookieLang = this.getCookie('localeInfo');
        if (cookieLang) {
            return cookieLang;
        }

        // 2. Check Browser Language
        const browserLang = navigator.language ? navigator.language.split('-')[0] : null;
        if (browserLang) {
            return browserLang;
        }

        // 3. Fallback to default
        return this.defaultLanguage;
    }

    private getCookie(name: string): string | null {
        const nameLenPlus = (name.length + 1);
        return document.cookie
            .split(';')
            .map(c => c.trim())
            .filter(cookie => {
                return cookie.substring(0, nameLenPlus) === `${name}=`;
            })
            .map(cookie => {
                return decodeURIComponent(cookie.substring(nameLenPlus));
            })[0] || null;
    }

    private setLangCookie(): void {
        const date = new Date();
        date.setTime(date.getTime() + (30 * 24 * 60 * 60 * 1000)); // 30days
        const expires = `expires=${date.toUTCString()}`;
        document.cookie = `localeInfo=${this.currentLang};${expires};path=/;SameSite=Lax`;
        this.eventManager.broadcast({ name: 'languageChanged' });
    }

    getCurrent(): Promise<string> {
        return Promise.resolve(this.currentLang);
    }
}
